import * as orderUtil from "./order"
import * as productUtil from "./product"
import { loadEventDetails, getSource } from "../Components/CameraFrame"
import {
  SOURCE_TYPE,
  EVENT_PERSON_SCAN,
  EVENT_ACTIVITY_DETECT,
  EVENT_ITEMS_PURCHASED,
  EVENT_LOYALTY,
  EVENT_SHELF_CHANGE,
  EVENT_RECEIPT_INPUT,
} from "../Config"

export const KIOSK_EVENT_TYPES = [EVENT_LOYALTY, EVENT_RECEIPT_INPUT]
export const SHELF_EVENT_TYPES = [EVENT_SHELF_CHANGE, EVENT_ACTIVITY_DETECT]

// DS-1189: shelf ids no longer carry the "sh-" prefix, so a source can no longer be
// identified as a shelf by its id string. The event type is the reliable discriminator —
// shelf events are EVENT_SHELF_CHANGE / EVENT_ACTIVITY_DETECT.
export const isShelfEvent = (event) => !!event && SHELF_EVENT_TYPES.includes(event.type)

const AUTO_RESOLVED = "auto"
const MT_RESOLVED = "mt"
const GESTURE_RESOLVED = "gesture"

/**
 * returns the index of task start time or events.length
 * (first occurance of task startTime > event.start)
 */
const findIndexOfTimeInEvents = (events, time) => {
  for (let i = 0; i < events.sortedIds.length; i++) {
    let eventId = events.sortedIds[i]
    let event = events.objects[eventId]

    if (event) {
      if (time < event.start) {
        return i
      }
    }
  }
  return events.sortedIds.length
}

const isShelfChangeEvent = (event) => {
  return event && event.type === EVENT_SHELF_CHANGE
}

export const filterFutureOrderProducts = (order, orderEvents, time, products) => {
  if (!order) {
    console.log("filterFutureOrderProducts no order")
    return
  }
  if (!orderEvents || !orderEvents.sortedIds || !orderEvents.objects) {
    console.log("filterFutureOrderProducts no orderEvents")
    return undefined
  }
  let isFamilyOrder = orderUtil.isFamilyOrder(order)
  let newOrder = { ...order }
  let timeIndex = findIndexOfTimeInEvents(orderEvents, time)
  for (let i = timeIndex; i < orderEvents.sortedIds.length; i++) {
    let eventId = orderEvents.sortedIds[i]
    let event = orderEvents.objects[eventId]
    if (isShelfChangeEvent(event) && Array.isArray(event.resolved_products)) {
      event.resolved_products.forEach((resolvedProduct) => {
        let productId = resolvedProduct.product_id
        let productQtyId = resolvedProduct.product_id.replace(
          productUtil.PROD_KEY_PREFIX,
          productUtil.PROD_QTY_KEY_PREFIX
        )
        let productWeight = resolvedProduct.weight
        let newProductWeight = order[productId] - productWeight
        if (newProductWeight !== 0) {
          newOrder[productId] = newProductWeight
        } else {
          delete newOrder[productId]
        }
        // change product quantity - inventory tracking
        if (products && products[productId] && products[productId].unit_weight) {
          let newProductQty = order[productQtyId] - productWeight / products[productId].unit_weight
          if (newProductQty !== 0) {
            newOrder[productQtyId] = newProductQty
          } else {
            delete newOrder[productQtyId]
          }
        }
        // remove the product weight from the sub family order as well
        if (isFamilyOrder) {
          let familyOrders = newOrder.familyOrder.orders
          let cartIdForEvent = event.cart
          if (familyOrders && familyOrders[cartIdForEvent]) {
            let subFamilyOrderForEvent = familyOrders[cartIdForEvent]
            let newProductWeightForSubFamilyOrder =
              subFamilyOrderForEvent[productId] - productWeight
            if (newProductWeightForSubFamilyOrder !== 0) {
              subFamilyOrderForEvent[productId] = newProductWeightForSubFamilyOrder
            } else {
              delete subFamilyOrderForEvent[productId]
            }
            // change product quantity - post inventory tracking
            if (products && products[productId] && products[productId].unit_weight) {
              let newProductQty =
                subFamilyOrderForEvent[productQtyId] -
                productWeight / products[productId].unit_weight
              if (newProductQty !== 0) {
                subFamilyOrderForEvent[productQtyId] = newProductQty
              } else {
                delete subFamilyOrderForEvent[productQtyId]
              }
            }
          }
        }
        console.log(
          `filterFutureOrderProducts Future ${productWeight} of ${productId} filtered from selected order down to ${newProductWeight}.`
        )
      })
    }
  }
  console.log(
    `filterFutureOrderProducts(order, orderEvents, time) and newOrder`,
    order,
    orderEvents,
    time,
    newOrder
  )
  return newOrder
}

export const getSourceEvent = (eventId, storeId) => {
  return loadEventDetails([eventId]).then((lstEvents) => {
    let events = []
    if (lstEvents) {
      events = lstEvents.eventsList

      let eventDetails = {}
      if (events && events.length > 0) {
        eventDetails = events[0]
      } else {
        return null
      }

      let sourceId = eventDetails ? eventDetails.source : ""
      let sourceType = ""
      if (eventDetails) {
        if (
          eventDetails.type === EVENT_SHELF_CHANGE ||
          eventDetails.type === EVENT_ACTIVITY_DETECT
        ) {
          sourceType = SOURCE_TYPE.SHELF
        } else if (
          eventDetails.type === EVENT_ITEMS_PURCHASED ||
          eventDetails.type === EVENT_LOYALTY ||
          eventDetails.type === EVENT_RECEIPT_INPUT
        ) {
          sourceType = SOURCE_TYPE.SOURCE
        }
      }
      return getSource(storeId, sourceId, sourceType).then((sourceDetails) => {
        return {
          cameraId: sourceDetails.cameraId,
          eventDetails: eventDetails,
          sourceDetails: sourceDetails.source,
        }
      })
    }
    return null
  })
}

export const getScanEventTime = (event) => {
  return event?.scan_time || event?.start
}

export const getEventTime = (event) => {
  if (!event) {
    return null
  }
  const type = event.type
  switch (type) {
    case EVENT_PERSON_SCAN:
      return getScanEventTime(event)
    case EVENT_ITEMS_PURCHASED:
    case EVENT_RECEIPT_INPUT:
      return event.end
    default:
      return event.start
  }
}

export const getEventLabel = (eventType) => {
  switch (eventType) {
    case EVENT_SHELF_CHANGE:
      return "Shelf Change"
    case EVENT_ITEMS_PURCHASED:
      return "Items Purchased"
    case EVENT_LOYALTY:
      return "Loyalty"
    case EVENT_RECEIPT_INPUT:
      return "Receipt Input"
    default:
      return ""
  }
}

/**
 * Returns true if @param event is resolved
 * @param {object} event
 * @returns {boolean}
 */
export const isEventResolved = (event) => {
  return [AUTO_RESOLVED, MT_RESOLVED, GESTURE_RESOLVED].includes(event?.state)
}

export const getEventDuration = (event) => {
  if (!event || !event.start || !event.end) return null

  const diff = event.end - event.start
  if (diff < 0) return null
  return diff
}
